import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import tw, { styled, css } from 'twin.macro'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { ArrowRightIcon, ArrowLeftIcon } from '@components/Icons'
import { PublishModeType } from './_core'
import { lightFormat, format, getHours, getMinutes } from 'date-fns'
import { zhTW } from 'date-fns/locale'
// 讓一週的起始日為週日, zhTW 的預設為週一
registerLocale('zhTW', { ...zhTW, options: { ...zhTW.options, weekStartsOn: 0 } })

type PublishTimeSettingProps = {
  scheduledAt: Date
  publishMode: PublishModeType
  onChangePublishMode: (event: React.ChangeEvent<Element>) => void
  pickerMode: 'idle' | 'date' | 'time'
  onChangePickerMode: (event: React.MouseEvent<Element>) => void
  onChangePickerDate: (date: Date) => void
  onChangePickerTime: (time: Date) => void
  timeOptions: Date[]
}

const isSameTime = (a: Date, b: Date) =>
  getHours(a) === getHours(b) && getMinutes(a) === getMinutes(b)

const PublishTimeSetting = (props: PublishTimeSettingProps) => {
  const {
    scheduledAt,
    onChangePublishMode,
    onChangePickerMode,
    onChangePickerDate,
    onChangePickerTime,
    publishMode,
    pickerMode,
    timeOptions,
  } = props
  const { t } = useTranslation('editor')
  const now = new Date()
  const timeZone = getTimezoneInfo(now)

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <div className="custom-header">
      <Arrow onClick={decreaseMonth}>
        <ArrowLeftIcon disabled={prevMonthButtonDisabled} />
      </Arrow>
      <span className="year-and-month">{lightFormat(date, 'yyyy 年 MM 月')}</span>
      <Arrow onClick={increaseMonth}>
        <ArrowRightIcon disabled={nextMonthButtonDisabled} />
      </Arrow>
    </div>
  )

  const timeOptionsRef = useRef(null)
  useEffect(() => {
    const $timeOption = timeOptionsRef.current as HTMLDivElement
    if (!$timeOption) return
    $timeOption.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'center' })
  }, [pickerMode, scheduledAt])

  return (
    <div tw="space-y-4">
      <div tw="space-y-4">
        <p tw="text-gray-800 text-h3-d leading-h1-d">{t(['publish.publishTime', '發布時間'])}</p>
        <PublishModeLabel htmlFor="publishNow">
          {t(['publish.publishNow', '直接發佈'])}
          <input
            type="radio"
            id="publishNow"
            name="publishTime"
            value="publishNow"
            checked={publishMode === PublishModeType.PUBLISH_NOW}
            onChange={onChangePublishMode}
          />
          <span className="check-mark" />
        </PublishModeLabel>
        <PublishModeLabel htmlFor="schedulePublish">
          {t(['publish.schedulePublish', '排程'])}
          <input
            type="radio"
            id="schedulePublish"
            name="publishTime"
            value="schedulePublish"
            checked={publishMode === PublishModeType.SCHEDULE_PUBLISH}
            onChange={onChangePublishMode}
          />
          <span className="check-mark" />
        </PublishModeLabel>
      </div>
      <div css={[tw`space-y-3`, publishMode !== PublishModeType.SCHEDULE_PUBLISH && tw`hidden`]}>
        <div tw="flex items-center relative z-mask">
          <StyledDatePicker
            $active={pickerMode === 'date'}
            data-mode="date"
            onClick={onChangePickerMode}
          >
            <label className="scheduled-date" tw="block md:hidden">
              {format(scheduledAt, 'yyyy/MM/dd', { locale: zhTW })}
              <input
                tw="opacity-0 w-0 h-0"
                type="date"
                min={format(now, 'yyyy-MM-dd', { locale: zhTW })}
                onChange={(e) => onChangePickerDate(e.target.valueAsDate)}
                value={format(scheduledAt, 'yyyy-MM-dd', { locale: zhTW })}
              />
            </label>
            <DatePicker
              selected={scheduledAt}
              onChange={(date, event) => {
                event.stopPropagation()
                onChangePickerDate(date as Date)
              }}
              className="scheduled-date"
              dateFormat="yyyy/MM/dd"
              renderCustomHeader={renderCustomHeader}
              minDate={now}
              showDisabledMonthNavigation
              locale="zhTW"
            />
          </StyledDatePicker>
          <StyledTimePicker
            $active={pickerMode === 'time'}
            data-mode="time"
            onClick={onChangePickerMode}
            tw="md:hidden relative"
          >
            <span className="scheduled-time">{format(scheduledAt, 'HH:mm', { locale: zhTW })}</span>
            <select
              className="scheduled-time"
              tw="absolute left-0 top-0 w-full h-full opacity-0"
              onChange={(e) => {
                const date = new Date(scheduledAt)
                const [hours, minutes] = e.target.value.split(':').map((x) => parseInt(x))
                date.setHours(hours)
                date.setMinutes(minutes)
                onChangePickerTime(date)
              }}
            >
              {timeOptions.map((time) => (
                <option key={time.toISOString()} value={format(time, 'HH:mm', { locale: zhTW })}>
                  {format(time, 'HH:mm', { locale: zhTW })}
                </option>
              ))}
            </select>
          </StyledTimePicker>
          <StyledTimePicker
            $active={pickerMode === 'time'}
            data-mode="time"
            onClick={onChangePickerMode}
            tw="hidden md:block"
          >
            <span className="scheduled-time">{format(scheduledAt, 'HH:mm', { locale: zhTW })}</span>
            {pickerMode === 'time' && (
              <div className="time-options" ref={timeOptionsRef}>
                {timeOptions.map((time) => (
                  <p
                    key={time.toISOString()}
                    className="option"
                    aria-selected={isSameTime(time, scheduledAt)}
                    onClick={(event) => {
                      event.stopPropagation()
                      onChangePickerTime(time)
                    }}
                  >
                    {format(time, 'HH:mm', { locale: zhTW })}
                  </p>
                ))}
              </div>
            )}
          </StyledTimePicker>
        </div>
        <p tw="text-gray-400 text-c2-d leading-c2-d">{timeZone}</p>
      </div>
    </div>
  )
}

const getTimezoneInfo = (now: Date) => {
  const gmtOffset = now.getTimezoneOffset() / 60
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  return `${timeZone} Time (GMT${gmtOffset >= 0 ? '' : '+'}${gmtOffset * -1})`
}

const pickerStyle = css`
  ${tw`box-border border cursor-pointer`}
  ${tw`text-c2-d leading-c2-d text-gray-800 text-center`}
  border-radius: 18px;
  height: 32px;
  outline: none;
  margin-right: 16px;
  font-family: PingFang TC;
  line-height: 32px;
`

const PublishModeLabel = styled.label`
  ${tw`relative flex items-center cursor-pointer text-h6-d leading-h6-d text-gray-800`}
  padding-left: 32px;
  height: 24px;
  user-select: none;

  /* Hide the browser's default radio button */
  input {
    ${tw`sr-only`}
  }

  /* Create a custom radio button */
  .check-mark {
    ${tw`rounded-full`};
    position: absolute;
    left: 0;
    height: 24px;
    width: 24px;
    border: 1px solid #cccccc;
  }

  input:checked ~ .check-mark {
    border: 1px solid #7d7af4;
  }

  /* Create the indicator (the dot/circle - hidden when not checked) */
  .check-mark::after {
    ${tw`absolute bg-primary-indigo hidden rounded-full`};
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    content: '';
    width: 12px;
    height: 12px;
  }

  /* Show the indicator (dot/circle) when checked */
  input:checked ~ .check-mark::after {
    ${tw`block`}
  }
`

const StyledDatePicker = styled.div<{
  $active: boolean
}>`
  .react-datepicker-wrapper {
    ${tw`hidden md:block`}
  }
  .custom-header {
    ${tw`flex justify-center items-center`};
    height: 50px;
    .year-and-month {
      ${tw`text-h6-d text-black`};
      line-height: 50px;
      padding: 0 24px;
      font-family: PingFang TC;
    }
  }
  .react-datepicker {
    ${tw`rounded-md`}
    border: 1px solid #E6E6E6;
    padding: 0 8px;
  }
  // 需要用 !import 才能覆蓋掉內建的 inline-style
  .react-datepicker-popper {
    left: -16px !important;
  }
  .react-datepicker__triangle {
    ${tw`hidden`}
  }
  .react-datepicker__header {
    ${tw`rounded-md border-0 bg-white`}
  }
  .scheduled-date {
    ${pickerStyle}
    width: 110px;
    border-color: ${(props) => (props.$active ? '#7D7AF4' : '#E1E1E1')};
  }
  .react-datepicker__month {
    margin-top: 0;
  }
  .react-datepicker__day-name,
  .react-datepicker__day {
    outline: none;
    font-family: PingFang TC;
    width: 32px;
    height: 32px;
    margin: 4px;
    line-height: 32px;
  }
  .react-datepicker__day-name {
    ${tw`text-gray-400`}
  }
  .react-datepicker__day {
    ${tw`text-gray-800 rounded-full`}
  }
  .react-datepicker__day--outside-month {
    ${tw`text-gray-400`}
  }
  .react-datepicker__day--disabled {
    ${tw`text-gray-100`}
  }
  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    ${tw`bg-primary-indigo text-white`}
  }
`

const Arrow = styled.div`
  ${tw`flex items-center justify-center`}
  ${tw`cursor-pointer rounded-full`}
  opacity: 1;
  width: 32px;
  height: 32px;
  border: 1px solid #999999;
  path {
    fill: #999999;
  }
`

const StyledTimePicker = styled.div<{
  $active: boolean
}>`
  ${pickerStyle};
  width: 65px;
  border-color: ${(props) => (props.$active ? '#7D7AF4' : '#E1E1E1')};
  .scheduled-time {
    line-height: 32px;
  }
  .time-options {
    ${tw`overflow-scroll rounded-md border border-gray-100 bg-white`}
    ${tw`text-black text-h5-d text-left`}
    ${tw`flex flex-col items-center`}
    height: 316px;
    width: 113px;
    padding: 16px 0;
    margin-top: 16px;
    font-family: PingFang TC;
    .option {
      ${tw`text-gray-800 text-center`}
      border-radius: 18px;
      height: 32px;
      width: 70px;
      line-height: 32px;
      margin-bottom: 12px;
      &[aria-selected='true'] {
        ${tw`text-white bg-primary-indigo`}
      }
    }
  }
`

export default PublishTimeSetting
